import { useLayoutEffect, type RefObject } from "react";

/** Keep the menu in its DOM/focus scope while lifting it out of clipping ancestors. */
export function useDropdownOverlay(ref: RefObject<HTMLDetailsElement | null>, active: boolean) {
  useLayoutEffect(() => {
    if (!active) return;
    const trigger = ref.current?.querySelector<HTMLElement>(":scope > summary");
    const menu = ref.current?.querySelector<HTMLElement>(":scope > [data-dropdown-overlay]");
    if (!trigger || !menu) return;
    const previousStyle = menu.getAttribute("style");
    menu.setAttribute("popover", "manual");
    menu.showPopover();
    const position = () => {
      const gap = 8, edge = 16, width = window.innerWidth, height = window.innerHeight;
      const rect = trigger.getBoundingClientRect();
      const mobile = width <= 700;
      const below = Math.max(0, height - rect.bottom - gap - edge);
      const above = Math.max(0, rect.top - gap - edge);
      Object.assign(menu.style, {
        position: "fixed", inset: "auto", margin: "0", boxSizing: "border-box",
        width: `${mobile ? width - edge * 2 : Math.min(280, width - edge * 2)}px`,
        maxWidth: "none", maxHeight: `${Math.max(0, Math.min(420, mobile ? height - edge * 2 : Math.max(above, below)))}px`,
        overflowY: "auto",
      });
      const size = menu.getBoundingClientRect();
      const start = getComputedStyle(trigger).direction === "rtl" ? rect.left : rect.right - size.width;
      const left = mobile ? edge : Math.max(edge, Math.min(start, width - size.width - edge));
      const top = mobile ? height - size.height - edge
        : below >= size.height || below >= above ? rect.bottom + gap : rect.top - size.height - gap;
      menu.style.left = `${left}px`;
      menu.style.top = `${Math.max(edge, Math.min(top, height - size.height - edge))}px`;
    };
    position();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
      if (menu.matches(":popover-open")) menu.hidePopover();
      menu.removeAttribute("popover");
      if (previousStyle === null) menu.removeAttribute("style");
      else menu.setAttribute("style", previousStyle);
    };
  }, [ref, active]);
}
